import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCheck, BarChart3, Loader2, TrendingUp as TrendingUpIcon, ArrowRight, Sparkles,
  ChevronDown, DollarSign, TrendingUp, Shield, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Pre-Qualification Agent ───
function PreQualificationAgent() {
  const [form, setForm] = useState({
    requested_credit_line: 50000,
    acs_score: 720,
    koji_staked: 10000,
    monthly_revenue_usdc: 6000,
    completed_jobs: 25,
    counterparty_count: 8,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/pre-qualification`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) setResult(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const tierColor = {
    'Sovereign':  'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    'Trusted':    'text-blue-400 bg-blue-500/10 border-blue-500/30',
    'Standard':   'text-amber-400 bg-amber-500/10 border-amber-500/30',
    'Watch':      'text-orange-400 bg-orange-500/10 border-orange-500/30',
    'Restricted': 'text-red-400 bg-red-500/10 border-red-500/30',
  };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Requested Credit Line ($)</label>
          <input data-testid="pq-credit-line" type="number" value={form.requested_credit_line}
            onChange={e => setForm({ ...form, requested_credit_line: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">ACS Score (300–850)</label>
          <input data-testid="pq-acs" type="number" min="300" max="850" value={form.acs_score}
            onChange={e => setForm({ ...form, acs_score: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">KOJIRU Staked</label>
          <input data-testid="pq-staked" type="number" min="0" value={form.koji_staked}
            onChange={e => setForm({ ...form, koji_staked: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Monthly Revenue (USDC)</label>
          <input data-testid="pq-revenue" type="number" min="0" value={form.monthly_revenue_usdc}
            onChange={e => setForm({ ...form, monthly_revenue_usdc: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Completed Jobs</label>
          <input data-testid="pq-jobs" type="number" min="0" value={form.completed_jobs}
            onChange={e => setForm({ ...form, completed_jobs: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Distinct Counterparties</label>
          <input data-testid="pq-counterparties" type="number" min="0" value={form.counterparty_count}
            onChange={e => setForm({ ...form, counterparty_count: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
      </div>
      <Button data-testid="pq-run-btn" onClick={run} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 py-3">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Evaluating...</> : <><UserCheck className="w-4 h-4 mr-2" /> Check Eligibility</>}
      </Button>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className={`p-4 rounded-xl border text-center ${tierColor[result.risk_tier] || tierColor['Restricted']}`}>
                <p className="text-xs font-medium mb-1">Risk Tier</p>
                <p data-testid="pq-tier" className="text-2xl font-bold">{result.risk_tier}</p>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <p className="text-xs text-gray-400 mb-1">Suggested APR</p>
                <p data-testid="pq-apr" className="text-2xl font-bold">{result.suggested_apr_pct}%</p>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <p className="text-xs text-gray-400 mb-1">Max Credit Line</p>
                <p data-testid="pq-max-line" className="text-2xl font-bold">${Math.round(result.max_credit_line).toLocaleString()}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-xs text-gray-400 mb-1">Monthly Service Fee</p>
                <p data-testid="pq-service-fee" className="text-xl font-bold">${Math.round(result.monthly_service_fee_estimate).toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-xs text-emerald-400 mb-1">Stake Utilization</p>
                <p data-testid="pq-stake-util" className="text-xl font-bold text-emerald-300">{result.stake_utilization_pct}%</p>
              </div>
            </div>

            {(result.strengths.length > 0 || result.improvements.length > 0) && (
              <div className="grid sm:grid-cols-2 gap-4">
                {result.strengths.length > 0 && (
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                    <p className="text-xs text-emerald-400 font-medium mb-2">Strengths</p>
                    {result.strengths.map((s, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1.5">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-gray-300">{s}</p>
                      </div>
                    ))}
                  </div>
                )}
                {result.improvements.length > 0 && (
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                    <p className="text-xs text-amber-400 font-medium mb-2">How to Improve</p>
                    {result.improvements.map((s, i) => (
                      <div key={i} className="flex items-start gap-2 mb-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-gray-300">{s}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {result.ai_assessment && (
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-xs text-blue-400 font-medium mb-2">AI Assessment</p>
                <p data-testid="pq-ai-text" className="text-sm text-gray-300 leading-relaxed">{result.ai_assessment}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Risk Assessment Agent ───
function RiskAssessmentAgent() {
  const [form, setForm] = useState({ credit_line_amount: 50000, agent_credit_score: 720, task_completion_rate: 85, staking_amount: 10000, time_on_protocol_days: 90 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/risk-assessment`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) setResult(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const recColor = result?.recommendation?.startsWith('FUND') ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' :
    result?.recommendation?.startsWith('CONDITIONAL') ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' :
    'text-red-400 bg-red-500/10 border-red-500/30';

  const healthColor = (h) => h === 'Healthy' ? 'text-emerald-400' : h === 'Adequate' ? 'text-blue-400' : h === 'Warning' ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Credit Line Amount ($)</label>
          <input data-testid="risk-credit-line" type="number" value={form.credit_line_amount}
            onChange={e => setForm({ ...form, credit_line_amount: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Agent Credit Score (ACS)</label>
          <input data-testid="risk-acs" type="number" min="300" max="850" value={form.agent_credit_score}
            onChange={e => setForm({ ...form, agent_credit_score: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Task Completion Rate (%)</label>
          <input data-testid="risk-task-rate" type="number" step="0.1" min="0" max="100" value={form.task_completion_rate}
            onChange={e => setForm({ ...form, task_completion_rate: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">KOJIRU Staked (Collateral)</label>
          <input data-testid="risk-staked" type="number" value={form.staking_amount}
            onChange={e => setForm({ ...form, staking_amount: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Days on Protocol</label>
          <input data-testid="risk-tenure" type="number" min="0" value={form.time_on_protocol_days}
            onChange={e => setForm({ ...form, time_on_protocol_days: +e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" />
        </div>
      </div>
      <Button data-testid="risk-run-btn" onClick={run} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 py-3">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing Risk...</> : <><BarChart3 className="w-4 h-4 mr-2" /> Assess Agent Risk</>}
      </Button>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className={`p-4 rounded-xl border text-center ${recColor}`}>
              <p data-testid="risk-recommendation" className="text-lg font-bold">{result.recommendation}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <p className="text-[10px] text-gray-500 mb-0.5">Risk Grade</p>
                <p data-testid="risk-grade" className="text-base font-bold">{result.risk_grade}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <p className="text-[10px] text-gray-500 mb-0.5">Default Probability</p>
                <p className="text-base font-bold">{result.default_probability_pct}%</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <p className="text-[10px] text-gray-500 mb-0.5">Expected Yield</p>
                <p className="text-base font-bold">{result.expected_annual_yield_pct}%/yr</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <p className="text-[10px] text-gray-500 mb-0.5">Risk-Adjusted Return</p>
                <p className="text-base font-bold">${Math.round(result.risk_adjusted_return).toLocaleString()}</p>
              </div>
            </div>

            {result.collateral_analysis && (
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-xs text-gray-400 font-medium mb-3">Collateral & Agent Profile</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-gray-500">Collateral Ratio</p>
                    <p className="text-sm font-bold">{result.collateral_analysis.collateral_ratio_pct}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Collateral Health</p>
                    <p className={`text-sm font-bold ${healthColor(result.collateral_analysis.collateral_health)}`}>{result.collateral_analysis.collateral_health}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Task Completion</p>
                    <p className="text-sm font-bold">{result.collateral_analysis.task_completion_rate}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Staked Collateral</p>
                    <p className="text-sm font-bold">${result.collateral_analysis.staked_collateral?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Credit Exposure</p>
                    <p className="text-sm font-bold">${result.collateral_analysis.credit_exposure?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Protocol Tenure</p>
                    <p className="text-sm font-bold">{result.collateral_analysis.protocol_tenure_days} days</p>
                  </div>
                </div>
              </div>
            )}

            {result.ai_assessment && (
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-xs text-blue-400 font-medium mb-2">AI Assessment</p>
                <p data-testid="risk-ai-text" className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{result.ai_assessment}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Yield Simulator (preview card → dedicated /yield-simulator page) ───
function YieldSimulatorPreview() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6" data-testid="yield-preview-card">
      <div className="p-6 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-cyan-500/5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-[10px] font-mono tracking-[0.24em] text-amber-400">NEW · LENDER OUTPUT CARD</span>
        </div>
        <h3 className="text-2xl font-bold mb-2 tracking-tight">Tell me how much I&apos;ll earn.</h3>
        <p className="text-sm text-gray-300 leading-relaxed mb-5 max-w-2xl">
          Project net yield, alpha vs USDC, and risk-adjusted return for a single bilateral agent credit line.
          Deterministic Monte Carlo (10,000 paths). Every projection writes a Checkpoint.
        </p>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="p-3 rounded-lg bg-white/[0.04] border border-white/[0.06]">
            <div className="text-[10px] font-mono text-zinc-500 tracking-wider mb-1">// HEADLINE</div>
            <div className="text-sm font-mono text-amber-400">Committee-ready</div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.04] border border-white/[0.06]">
            <div className="text-[10px] font-mono text-zinc-500 tracking-wider mb-1">// BANDS</div>
            <div className="text-sm font-mono text-cyan-400">P10 / P50 / P90</div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.04] border border-white/[0.06]">
            <div className="text-[10px] font-mono text-zinc-500 tracking-wider mb-1">// ALPHA</div>
            <div className="text-sm font-mono text-emerald-400">vs USDC baseline</div>
          </div>
        </div>
        <Button
          onClick={() => navigate('/yield-simulator')}
          className="bg-gradient-to-r from-amber-500 to-amber-600 hover:brightness-110 text-black font-semibold"
          data-testid="yield-preview-cta"
        >
          Open Full Yield Simulator <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
      <div className="text-xs text-zinc-500 font-mono">
        // The full Yield Simulator is a dedicated page at <span className="text-cyan-400">/yield-simulator</span> with 8-input panel, fan chart, sensitivity sliders, and Save &amp; Track backtest.
      </div>
    </div>
  );
}

// ─── Main Page ───
const agents = [
  { id: 'risk', title: 'Agent Risk Assessment', icon: BarChart3, desc: 'Evaluate an agent\'s creditworthiness. Default probability, yield modeling, and funding recommendation.', color: 'from-amber-500 to-orange-500', component: RiskAssessmentAgent },
  { id: 'prequalification', title: 'Agent Pre-Qualification', icon: UserCheck, desc: 'Pre-qualify an agent for a credit line based on ACS score, staking, and financial profile.', color: 'from-emerald-500 to-green-500', component: PreQualificationAgent },
  { id: 'yield', title: 'Credit Yield Simulator', icon: TrendingUpIcon, desc: 'Project net yield, alpha vs USDC, and risk-adjusted return for a bilateral agent credit line. Monte Carlo · Deterministic · On-chain.', color: 'from-amber-400 to-cyan-500', component: YieldSimulatorPreview },
];

export default function AIAgentsLive() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  const [active, setActive] = useState(
    agents.some(a => a.id === tabParam) ? tabParam : 'risk'
  );

  const ActiveComponent = agents.find(a => a.id === active)?.component;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="pt-24 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest text-emerald-400 uppercase mb-3">Agent Credit AI Tools</p>
            <h1 data-testid="ai-agents-title" className="text-3xl sm:text-4xl font-bold mb-3">
              Intelligent Agent Analysis
            </h1>
            <p className="text-gray-400 max-w-xl mx-auto text-sm">
              Three AI tools to evaluate agents, assess credit risk, and project lender yield.
            </p>
          </motion.div>

          {/* Agent Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="agent-tabs">
            {agents.map(a => (
              <button key={a.id} data-testid={`tab-${a.id}`} onClick={() => setActive(a.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  active === a.id ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}>
                <a.icon className="w-4 h-4" />
                {a.title}
              </button>
            ))}
          </div>

          {/* Active Agent */}
          <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="p-6 sm:p-8 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
            <div className="flex items-center gap-3 mb-6">
              {(() => { const a = agents.find(x => x.id === active); return (
                <>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${a.color} flex items-center justify-center`}>
                    <a.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">{a.title} Agent</h2>
                    <p className="text-xs text-gray-500">{a.desc}</p>
                  </div>
                </>
              ); })()}
            </div>
            {ActiveComponent && <ActiveComponent />}
          </motion.div>

          {/* Future Tools */}
          <div className="mt-10">
            {[
              { title: 'Portfolio Analytics', desc: 'Monitor credit line performance, agent repayment trends, and portfolio-level risk metrics.', icon: '5' },
            ].map(a => (
              <div key={a.title} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] opacity-50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-400">{a.title}</h3>
                  <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">Coming Soon</span>
                </div>
                <p className="text-xs text-gray-600">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
